import React, { FC, useContext, useEffect, useMemo, useState, useCallback } from "react";
import "./InputChat.css";
import "easymde/dist/easymde.min.css";
import { Context } from "../../..";
import { IFunctionPermissions } from "../../../common/api_interfaces/role/IFunctionPermissions";
import { AppContext } from "../../../App";
import TextEditor from "../TextEditor/TextEditor";
import axios from "axios";
import { hasPermission } from "../../../helpers/hasPermisson";
import { createArgumentIfDefined, getOptionsQueryParams, IArgument, IOptionsQueryParams, urlFiller } from "../../../helpers/paramsAutoFiller";
import { useNavigate} from "react-router-dom";
import TaskActions, { TaskActionOption, Priority, Type } from "../../Task/TaskActions/TaskActions";
import { IDashBoard } from "../../../common/api_interfaces/board/IDashBoard";
import { IMessageActions } from "../../../common/api_interfaces/actions/IMessageActions";
import ChatMessages from "./ChatMessages";
import { TaskContext } from "../../../contexts/TaskContext";
import { IColumn } from "../../../common/api_interfaces/column/IColumn";
import { IMessage } from "../../../common/api_interfaces/chat/IMessage";
import { IMessageGroup } from "../../../common/api_interfaces/chat/IMessageGroup";
import { ITeamMemberDisplay } from "../../../common/api_interfaces/project/ITeamDisplay";

interface IInputChat {
    userAccess: IFunctionPermissions[];
    chatId: number;
    setMessagesCount?: React.Dispatch<React.SetStateAction<number>>;
    projectId?: number;
    messageUrlQueryParams?: string;
    optionsQueryParams: IOptionsQueryParams;
    showTaskActions?: boolean;
    taskId?: number;
    boardId?: number;
    onActionsExecuted?: () => void;
}

const InputChat: FC<IInputChat> = ({
    userAccess,
    chatId,
    projectId,
    setMessagesCount,
    messageUrlQueryParams,
    optionsQueryParams,
    showTaskActions,
    taskId,
    boardId,
    onActionsExecuted,
}) => {
    // ------ Константы -------
    const { store } = useContext(Context);
    const { showToast, handleShowLoader } = useContext(AppContext);
    const { getAllTaskPerson, allPerson } = useContext(TaskContext);
    const navigate = useNavigate();

    // Основные состояния
    const [messageGroups, setMessageGroups] = useState<IMessageGroup[]>([]);
    const [waitingMessagesLoad, setWaitingMessagesLoad] = useState(true);
    const [editContent, setEditContent] = useState<string>("");
    const [modeTextEditor, setModeTextEditor] = useState<"edit" | "create">("create");
    const [idEditMessage, setIdEditMessage] = useState(-1);

    // Действия с задачей
    const [selectedActions, setSelectedActions] = useState<TaskActionOption[]>([]);
    const [currentColumn, setCurrentColumn] = useState<IColumn>();
    const [currentBoard, setCurrentBoard] = useState<Omit<IDashBoard, "projectId" | "isProxyBoard">>();
    const [currentResponsible, setCurrentResponsible] = useState<ITeamMemberDisplay | null>();
    const [currentType, setCurrentType] = useState<Type>();
    const [currentPriority, setCurrentPriority] = useState<Priority>();

    // Мемоизированные значения
    const canCreateTaskChatMessage = useMemo(() => hasPermission(userAccess, "TaskChatAction", "create"), [userAccess]);
    const canEditTaskChatMessage = useMemo(() => hasPermission(userAccess, "TaskChatAction", "edit"), [userAccess]);
    const canEditTask = useMemo(() => hasPermission(userAccess, "TaskAction", "edit"), [userAccess]);
    
    const actionsOpen = useMemo(() => selectedActions.length > 0, [selectedActions]);
    const actionSelected = useMemo(() => 
        currentColumn !== undefined ||
        currentBoard !== undefined ||
        currentResponsible !== undefined ||
        currentType !== undefined ||
        currentPriority !== undefined,
        [currentColumn, currentBoard, currentResponsible, currentType, currentPriority]
    );

    // ------ Функции компонента --------

    const clearTaskActions = useCallback(() => {
        setSelectedActions([]);
        setCurrentColumn(undefined);
        setCurrentBoard(undefined);
        setCurrentResponsible(undefined);
        setCurrentType(undefined);
        setCurrentPriority(undefined);
    }, []);

    const closeEditMessage = useCallback(() => {
        setIdEditMessage(-1);
        setModeTextEditor("create");
        setEditContent("");
    }, []);

    const loadChatMessages = useCallback(async () => {
        try {
            const response = await store.chatService.getChatMessages(chatId);
            const groups = response.data;
            
            if (groups) {
                // Сортировка сообщений внутри групп
                const sortedGroups = groups.map(group => ({
                    ...group,
                    messages: group.messages.sort(
                        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
                    )
                })).sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                setMessageGroups(sortedGroups);
                
                if (setMessagesCount) {
                    const totalMessages = sortedGroups.reduce((acc, group) => acc + group.messages.length, 0);
                    setMessagesCount(totalMessages);
                }

                // Обработка редактирования сообщения из URL параметров
                if (canEditTaskChatMessage) {
                    const options = getOptionsQueryParams(window.location.search);
                    const shouldEditMessage = options.page === "task" && 
                        (options.entity === "chat" || options.entity === "chat-chek-list") && 
                        options.action === "edit";

                    if (shouldEditMessage && options.entityId) {
                        const targetMessage = sortedGroups
                            .flatMap(group => group.messages)
                            .find(message => message.id === options.entityId);
                        
                        if (targetMessage) {
                            editMessage(targetMessage);
                        }
                    }
                }
            }
        } catch (err: any) {
            const errorMessage = axios.isAxiosError(err) 
                ? err.response?.data?.message 
                : "Ошибка при загрузке сообщений чата";
            showToast(errorMessage);
        }
    }, [chatId, canEditTaskChatMessage, setMessagesCount, store.chatService, showToast]);

    const saveEditMessage = useCallback(async (editContent: string, mentionPersons: number[]) => {
        if (!canEditTaskChatMessage) {
            showToast("У вас нет прав для редактирования сообщений");
            return;
        }

        handleShowLoader(true);

        try {
            const messageInt = {
                message: editContent,
                messageId: idEditMessage,
                mentionsIds: mentionPersons || [],
            };

            let resp = await store.chatService.saveEditMessage(messageInt);
            await loadChatMessages();
            closeEditMessage();

            return resp;
        } catch (e: any) {
            showToast(`Произошла ошибка! ${e.response?.data?.message || e.message}`);
            return e.response;
        } finally {
            handleShowLoader(false);
        }
    }, [canEditTaskChatMessage, idEditMessage, store.chatService, loadChatMessages, closeEditMessage, showToast, handleShowLoader]);

    const saveMessage = useCallback(async (content: string, mentionPersons: number[]) => {
        if (!canCreateTaskChatMessage) {
            showToast("У вас нет прав для отправки сообщений");
            return;
        }

        handleShowLoader(true);

        try {
            if (allPerson?.group.find(xx => xx.id === currentResponsible?.id)) {
                showToast("Уберите исполнителя из подписчиков для сохранения");
                return;
            }

            const response = await store.chatService.sendMessage({ 
                message: content, 
                chatId, 
                mentionsIds: mentionPersons 
            });

            // Обработка действий с задачей
            if (actionSelected && boardId !== undefined && taskId !== undefined) {


                const reqBody: IMessageActions = {
                    boardId,
                    tasks: [{ taskId, messageId: response.data.messageId }],
                    actionParams: {
                        changeResponsibleParam: currentResponsible !== undefined ? { responsibleId: currentResponsible ? currentResponsible.id : null } : undefined,
                        changePriorotyParam: currentPriority ? { priorityId: currentPriority.id } : undefined,
                        changeTypeParam: currentType ? { typeId: currentType.id } : undefined,
                        changeColumnParam: currentColumn 
                            ? { columnId: currentColumn.id } 
                            : currentBoard 
                            ? { boardId: currentBoard.id } 
                            : undefined,
                    },
                };
                
                await store.taskService.executeActions(reqBody);
                
                await getAllTaskPerson();
                if (currentBoard) {
                    navigate(`/task/${taskId}`);
                }

                onActionsExecuted?.();
            }

            clearTaskActions();
            await loadChatMessages();

            return response;
        } catch (e: any) {
            showToast(`Произошла ошибка! ${e.response?.data?.message || e.message}`);
            return e.response;
        } finally {
            handleShowLoader(false);
        }
    }, [
        canCreateTaskChatMessage, chatId, actionSelected, boardId, taskId, 
        currentResponsible, currentPriority, currentType, currentColumn, currentBoard,
        store.chatService, store.taskService, navigate, onActionsExecuted, clearTaskActions, 
        loadChatMessages, showToast, handleShowLoader, allPerson
    ]);

    const editMessage = useCallback((message: IMessage) => {
        if (!canEditTaskChatMessage) {
            showToast("У вас нет прав на редактирование комментариев");
            return;
        }
        if (actionsOpen) {
            showToast("При добавлении действий нельзя редактировать комментарии");
            return;
        }

        setCurrentColumn(undefined);
        setCurrentBoard(undefined);
        setCurrentResponsible(undefined);
        setCurrentType(undefined);
        setCurrentPriority(undefined);

        setEditContent(message.text);
        setModeTextEditor("edit");
        setIdEditMessage(message.id);
        

        const urlArguments = [
            createArgumentIfDefined("action", "edit"),
            createArgumentIfDefined("entity", optionsQueryParams.entity),
            createArgumentIfDefined("entityId", message.id),
            createArgumentIfDefined("page", optionsQueryParams.page),
        ].filter(Boolean) as IArgument[];

        urlFiller(
            window.location.href,
            { arguments: urlArguments, fragment: [] },
            false,
            navigate
        );
    }, [canEditTaskChatMessage, actionsOpen, optionsQueryParams, navigate, showToast]);

    const onSend = useCallback(async (content: string, mention: number[]) => {
        if (!actionSelected && (content.trim() === '' || content === undefined || content === "<p><br></p>")) {
            showToast("Нельзя отправить пустое сообщение!");
            return;
        }

        try {
            if (modeTextEditor === "edit") {
                if (actionSelected) {
                    showToast("Нельзя совершать действия и редактировать");
                    return;
                }
                let resp = await saveEditMessage(content, mention);
                return resp;
            } else {
                let resp = await saveMessage(content, mention);
                return resp;
            }
        } catch (e: any) {
            return e.response;
        }
    }, [modeTextEditor, actionSelected, saveEditMessage, saveMessage, showToast]);

    // ------ Эффекты -------

    // Загрузка сообщений при монтировании и изменении chatId
    useEffect(() => {
        let isMounted = true;
        
        const loadMessages = async () => {
            try {
                await loadChatMessages();
            } catch (error) {
                console.error("Ошибка загрузки сообщений:", error);
            } finally {
                if (isMounted) {
                    setWaitingMessagesLoad(false);
                }
            }
        };

        loadMessages();

        return () => {
            isMounted = false;
        };
    }, [chatId, loadChatMessages]);

    // Скроллинг к якорю после загрузки сообщений
    useEffect(() => {
        if (waitingMessagesLoad) return;

        const hash = window.location.hash;
        if (hash) {
            const element = document.querySelector(hash);
            element?.scrollIntoView({
                block: "center",
                behavior: "smooth",
            });
        }
    }, [waitingMessagesLoad]);

    return (
        <div className="input--chat__block">
            <ChatMessages
                userAccess={userAccess}
                messageUrlQueryParams={messageUrlQueryParams}
                messageGroups={messageGroups}
                onDeleteMessage={loadChatMessages}
                onEditMessage={editMessage}
                idEditMessage={idEditMessage}
                modeTextEditor={modeTextEditor}
            />
            
            {showTaskActions && canEditTask && modeTextEditor !== "edit" && (
                <div>
                    <TaskActions
                        selectedActions={selectedActions}
                        setSelectedActions={setSelectedActions}
                        onChangeColumn={(column) => {
                            setCurrentColumn(column);
                            setCurrentBoard(undefined);
                        }}
                        onChangeBoard={(board) => {
                            setCurrentBoard(board);
                            setCurrentColumn(undefined);
                        }}
                        onChangePriority={setCurrentPriority}
                        onChangeResponsible={setCurrentResponsible}
                        onChangeType={setCurrentType}
                    />
                </div>
            )}
            
            <TextEditor
                userAcess={{
                    edit: canEditTaskChatMessage,
                    create: canCreateTaskChatMessage
                }}
                workMode={modeTextEditor}
                callBackSave={onSend}
                callBackClose={closeEditMessage}
                mention={{ projectId: projectId! }}
                editContent={editContent}
                chatId={chatId}
                optionsQueryParams={{
                    page: optionsQueryParams.page,
                    entity: optionsQueryParams.entity,
                    action: modeTextEditor,
                    entityId: modeTextEditor === "edit" ? idEditMessage : undefined,
                    cheklistId: optionsQueryParams.cheklistId,
                }}
            />
        </div>
    );
};

export default InputChat;